import React, { useEffect, useState } from 'react';
import { adminAPI } from '@/services/adminAPI';
import { toast } from 'sonner';
import { Cpu, RefreshCw, RotateCcw, PlayCircle, CheckCircle2, AlertTriangle, Clock, Pin, PinOff, Zap } from 'lucide-react';
import { Button } from '@/components/ui/button';

const providerLabels = {
  abacus: { label: 'Abacus RouteLLM', tone: 'text-emerald-500', desc: 'Primary — routes to best underlying model' },
  openai: { label: 'OpenAI GPT', tone: 'text-sky-500', desc: 'Fallback 1' },
  anthropic: { label: 'Anthropic Claude', tone: 'text-amber-500', desc: 'Fallback 2' },
  gemini: { label: 'Google Gemini', tone: 'text-indigo-500', desc: 'Fallback 3' },
  deepseek: { label: 'DeepSeek', tone: 'text-fuchsia-500', desc: 'Fallback 4' },
  emergent: { label: 'Emergent Key', tone: 'text-slate-500', desc: 'Final safety net' },
};

function formatTs(ts) {
  if (!ts) return '—';
  try { return new Date(ts * 1000).toLocaleString(); } catch { return '—'; }
}

export default function AdminLLM() {
  const [providers, setProviders] = useState([]);
  const [sticky, setSticky] = useState(null);
  const [loading, setLoading] = useState(false);
  const [testingAll, setTestingAll] = useState(false);
  const [testingProvider, setTestingProvider] = useState(null);
  const [pinningProvider, setPinningProvider] = useState(null);
  const [testResults, setTestResults] = useState({}); // {provider: {ok, reply/error}}
  const [routedResult, setRoutedResult] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await adminAPI.getLLMStatus();
      setProviders(r.providers || []);
      setSticky(r.sticky_provider || null);
    } catch (e) {
      toast.error('Failed to load LLM status');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const runRoutedTest = async () => {
    setTestingAll(true);
    setRoutedResult(null);
    try {
      const r = await adminAPI.testLLM();
      setRoutedResult(r);
      setProviders(r.providers || []);
      if (r.ok) {
        toast.success(`Routed test passed via ${r.provider || 'active'} — reply: ${r.reply}`);
        setSticky(r.provider || null);
      } else {
        toast.error(`Routed LLM test failed: ${r.error || 'unknown error'}`);
      }
    } catch (e) {
      toast.error('Test call failed');
    } finally { setTestingAll(false); }
  };

  const runProviderTest = async (provider) => {
    setTestingProvider(provider);
    setTestResults((prev) => ({ ...prev, [provider]: null }));
    try {
      const r = await adminAPI.testLLM(provider);
      setProviders(r.providers || providers);
      setTestResults((prev) => ({ ...prev, [provider]: r }));
      if (r.ok) toast.success(`${provider} OK — ${r.reply}`);
      else toast.error(`${provider} failed`);
    } catch (e) {
      toast.error(`${provider} test failed`);
      setTestResults((prev) => ({ ...prev, [provider]: { ok: false, error: 'Request failed' } }));
    } finally { setTestingProvider(null); }
  };

  const pin = async (provider) => {
    setPinningProvider(provider || 'clear');
    try {
      const r = await adminAPI.setLLMSticky(provider);
      setSticky(r.sticky_provider || null);
      setProviders(r.providers || providers);
      toast.success(provider ? `${provider} set as active` : 'Sticky cleared — auto-route on next call');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to update active provider');
    } finally { setPinningProvider(null); }
  };

  const resetCooldown = async (provider) => {
    try {
      await adminAPI.resetLLMCooldown(provider);
      toast.success(`Cooldown reset for ${provider || 'all providers'}`);
      await load();
    } catch (e) {
      toast.error('Reset failed');
    }
  };

  const stickyMeta = sticky ? (providerLabels[sticky] || { label: sticky, tone: 'text-slate-500' }) : null;

  return (
    <div data-testid="admin-llm">
      <div className="flex items-start justify-between mb-4 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 dark:text-white mb-1 flex items-center gap-2">
            <Cpu className="w-6 h-6" /> LLM Router
          </h1>
          <p className="text-slate-500 dark:text-zinc-500 text-sm">
            Failover chain across Abacus, OpenAI, Claude, Gemini, DeepSeek, Emergent.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="admin-llm-refresh">
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
          <Button size="sm" onClick={runRoutedTest} disabled={testingAll} data-testid="admin-llm-test">
            <PlayCircle className="w-4 h-4 mr-1" /> {testingAll ? 'Testing…' : 'Run routed test'}
          </Button>
          <Button variant="outline" size="sm" onClick={() => resetCooldown(null)} data-testid="admin-llm-reset-all">
            <RotateCcw className="w-4 h-4 mr-1" /> Reset all
          </Button>
        </div>
      </div>

      {/* Current active LLM banner */}
      <div
        className="mb-6 p-4 rounded-lg border border-emerald-200 dark:border-emerald-900 bg-emerald-50 dark:bg-emerald-950/30 flex flex-wrap items-center justify-between gap-3"
        data-testid="admin-llm-active-banner"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-emerald-500/10 flex items-center justify-center">
            <Zap className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
          </div>
          <div>
            <p className="text-xs text-emerald-700 dark:text-emerald-400 uppercase font-medium tracking-wide">Currently active</p>
            {sticky && stickyMeta ? (
              <p className={`font-semibold ${stickyMeta.tone}`} data-testid="admin-llm-current-sticky">
                {stickyMeta.label} <span className="text-xs text-slate-500 dark:text-zinc-400 font-normal">({sticky})</span>
              </p>
            ) : (
              <p className="text-slate-700 dark:text-zinc-300 font-medium" data-testid="admin-llm-current-sticky">
                No sticky provider yet — next call will pick the first available in the chain.
              </p>
            )}
          </div>
        </div>
        {sticky && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => pin(null)}
            disabled={pinningProvider === 'clear'}
            data-testid="admin-llm-clear-sticky"
          >
            <PinOff className="w-4 h-4 mr-1" /> Clear sticky
          </Button>
        )}
      </div>

      {routedResult && (
        <div className={`mb-6 p-3 rounded-lg border text-sm ${routedResult.ok ? 'bg-emerald-50 dark:bg-emerald-950/30 border-emerald-200 dark:border-emerald-900 text-emerald-700 dark:text-emerald-300' : 'bg-red-50 dark:bg-red-950/30 border-red-200 dark:border-red-900 text-red-700 dark:text-red-300'}`}>
          {routedResult.ok ? (
            <><CheckCircle2 className="inline w-4 h-4 mr-1" /> Routed via <b>{routedResult.provider || '—'}</b> — Reply: <span className="font-mono">{routedResult.reply}</span></>
          ) : (
            <><AlertTriangle className="inline w-4 h-4 mr-1" /> {routedResult.error}</>
          )}
        </div>
      )}

      <div className="grid gap-3">
        {providers.map((p, idx) => {
          const meta = providerLabels[p.provider] || { label: p.provider, tone: 'text-slate-500', desc: '' };
          const isSticky = p.is_sticky || sticky === p.provider;
          const result = testResults[p.provider];
          const busyTest = testingProvider === p.provider;
          const busyPin = pinningProvider === p.provider;
          return (
            <div
              key={p.provider}
              className={`bg-white dark:bg-[#111114] border rounded-lg p-4 flex flex-wrap items-start justify-between gap-3 ${isSticky ? 'border-emerald-400 dark:border-emerald-700 ring-1 ring-emerald-300/40 dark:ring-emerald-800/60' : 'border-slate-200 dark:border-zinc-800/50'}`}
              data-testid={`admin-llm-row-${p.provider}`}
            >
              <div className="flex-1 min-w-[220px]">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <span className="text-xs font-mono text-slate-400 dark:text-zinc-600">#{idx + 1}</span>
                  <span className={`font-medium ${meta.tone}`}>{meta.label}</span>
                  {isSticky && (
                    <span className="text-xs px-2 py-0.5 rounded bg-emerald-500 text-white flex items-center gap-1" data-testid={`admin-llm-active-${p.provider}`}>
                      <Zap className="w-3 h-3" /> Active
                    </span>
                  )}
                  {!p.configured && (
                    <span className="text-xs px-2 py-0.5 rounded bg-slate-100 dark:bg-zinc-900 text-slate-500 dark:text-zinc-500">Not configured</span>
                  )}
                  {p.configured && !p.in_cooldown && !isSticky && (
                    <span className="text-xs px-2 py-0.5 rounded bg-emerald-50 dark:bg-emerald-950 text-emerald-600 dark:text-emerald-400 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Healthy</span>
                  )}
                  {p.in_cooldown && (
                    <span className="text-xs px-2 py-0.5 rounded bg-amber-50 dark:bg-amber-950 text-amber-600 dark:text-amber-400 flex items-center gap-1"><Clock className="w-3 h-3" /> Cooldown {p.cooldown_seconds_left}s</span>
                  )}
                </div>
                <p className="text-xs text-slate-500 dark:text-zinc-500 mb-2">{meta.desc}</p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-1 text-xs text-slate-600 dark:text-zinc-400">
                  <div>Calls: <span className="font-mono text-slate-800 dark:text-zinc-200">{p.total_calls}</span></div>
                  <div>Failures: <span className={`font-mono ${p.total_failures ? 'text-red-500' : 'text-slate-800 dark:text-zinc-200'}`}>{p.total_failures}</span></div>
                  <div className="col-span-2 truncate">Last used: <span className="font-mono">{formatTs(p.last_used_at)}</span></div>
                </div>
                {p.last_error && (
                  <div className="mt-2 text-xs text-red-600 dark:text-red-400 font-mono break-all" data-testid={`admin-llm-error-${p.provider}`}>
                    {p.last_error}
                  </div>
                )}
                {result && (
                  <div className={`mt-2 text-xs font-mono rounded px-2 py-1 ${result.ok ? 'bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300' : 'bg-red-50 dark:bg-red-950/40 text-red-600 dark:text-red-300'}`} data-testid={`admin-llm-test-result-${p.provider}`}>
                    {result.ok ? `✓ ${result.reply}` : `✗ ${result.error}`}
                  </div>
                )}
              </div>
              <div className="flex flex-col gap-2 items-end">
                <div className="flex gap-2 flex-wrap justify-end">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => runProviderTest(p.provider)}
                    disabled={!p.configured || busyTest}
                    data-testid={`admin-llm-test-${p.provider}`}
                  >
                    <PlayCircle className="w-3 h-3 mr-1" /> {busyTest ? 'Testing…' : 'Test'}
                  </Button>
                  {!isSticky && (
                    <Button
                      size="sm"
                      onClick={() => pin(p.provider)}
                      disabled={!p.configured || busyPin}
                      data-testid={`admin-llm-pin-${p.provider}`}
                    >
                      <Pin className="w-3 h-3 mr-1" /> {busyPin ? 'Setting…' : 'Set active'}
                    </Button>
                  )}
                  {p.in_cooldown && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => resetCooldown(p.provider)}
                      data-testid={`admin-llm-reset-${p.provider}`}
                    >
                      <RotateCcw className="w-3 h-3 mr-1" /> Reset
                    </Button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
